import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// CSS imports
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import './css/afrofuturist.css';
import './css/style.css';
import './css/carousel-custom.css';
import './css/responsive.css';

// Component imports
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Spinner from './common/spinner';
import BackToTop from './common/BackToTop';
import Layout from './components/Layout';
import HomePage from './Pages/Home';
import About from './Pages/about';
import Courses from './Pages/courses';
import Team from './Pages/Team';
import Categories from './components/Categories';
import Contact from './Pages/contact';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import CourseEnroll from './components/CourseEnroll';
import CourseContent from './components/CourseContent';
import InstructorDashboard from './Pages/InstructorDashboard';
import Testimonials from './Pages/testimonials';
import PrivateRoute from './context/PrivateRoute';
import BecomeInstructor from './Pages/BecomeInstructor';
import InstructorLogin from './Pages/InstructorLogin';
import Privacy from './Pages/Privacy';
import Terms from './Pages/Terms';
import Faq from './Pages/Faq';
import Certificates from './Pages/Certificates';
import CourseDetail from './Pages/CourseDetail';
import CertificateView from './Pages/CertificateView';
import Cookies from './Pages/Cookies';
import Help from './Pages/Help';

const NotFound = () => (
    <div className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
        <div className="container text-center">
            <div className="row justify-content-center">
                <div className="col-lg-6">
                    <i className="bi bi-exclamation-triangle display-1 text-primary"></i>
                    <h1 className="display-1">404</h1>
                    <h1 className="mb-4">Page Not Found</h1>
                    <p className="mb-4">We're sorry, the page you have looked for does not exist in our website! Maybe go to our home page or try to use a search?</p>
                    <a className="btn btn-primary rounded-pill py-3 px-5" href="/">Go Back To Home</a>
                </div>
            </div>
        </div>
    </div>
);

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        {/* Favicon */}
        <link href="/img/favicon.ico" rel="icon" />

        {/* Google Web Fonts */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Playfair+Display:ital,wght@0,600;0,700;1,600;1,700&display=swap" rel="stylesheet" />
      </Helmet>

      <Spinner />
      <Navbar />
      
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<About />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/course/:id" element={<CourseDetail />} />
          <Route path="/courses/:category" element={<Courses />} />
          <Route path="/team" element={<Team />} />
          <Route path="/team/featured" element={<Team featured={true} />} />
          <Route path="/team/join" element={<BecomeInstructor />} />
          <Route path="/testimonial" element={<Testimonials />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/auth" element={<Auth />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:id/enroll"
            element={
              <PrivateRoute>
                <CourseEnroll />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:id/content"
            element={
              <PrivateRoute>
                <CourseContent />
              </PrivateRoute>
            }
          />
          <Route
            path="/instructorDashboard"
            element={
              <PrivateRoute requireInstructor={true}>
                <InstructorDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/certificates"
            element={
              <PrivateRoute>
                <Certificates />
              </PrivateRoute>
            }
          />
          <Route
            path="/certificate/:courseId/:studentId"
            element={
              <PrivateRoute>
                <CertificateView />
              </PrivateRoute>
            }
          />
          <Route path="/instructor/login" element={<InstructorLogin />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/help" element={<Help />} />

          {/* Catch-all route for 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
      
      <Footer />
      <BackToTop />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    
    </HelmetProvider>
  );
}

export default App;